<script lang="ts">
    import type { SettingsPages, SettingsPage as SettingsPageT, Setting } from ".";

    import { setContext, createEventDispatcher, onMount } from "svelte";
    import SettingsPage from "./SettingsPage.svelte";
    import { writable } from "svelte/store";
    import { filterSetting } from "./createFilter";
    import { linkHandling } from "./link";

    const dispatch = createEventDispatcher<{ close: {}, save: {} }>();

    let searchElem: HTMLInputElement;
    onMount(() => searchElem.focus());

    export let pages: SettingsPages;

    export let startPage: string | undefined = undefined;

    let settings = pages.pages;
    let current = create();

    function create() {
        const s = settings.map(s => s.settings.value);

        return s.map((page, index) => {
            let nPage = {} as  Record<string, Setting>;
            for (const key in page) {
                nPage[key] = {
                    ...page[key],
                    value: structuredClone(page[key].value)
                }
            }
            return nPage;
        });
    }

    let activeIndex = settings.findIndex(page => page.id === startPage);
    activeIndex = activeIndex < 0 ? 0 : activeIndex;
    let activePage = settings[activeIndex] as SettingsPageT;

    let search = writable("");
    setContext("search", search);

    $: filteredTabs = filterTabs($search);

    function filterTabs(search: string) {
        const tabs = settings.map((_, index) => filterTab(index, search));
        for (let i = 0; i < settings.length; i++) {
            tabs[i] = filterTab(i, search);
        }

        if (tabs[activeIndex]) {
            const first = tabs.findIndex(f => !f);
            if (first >= 0) changeTab(first);
        }

        return tabs;
    }

    function filterTab(index: number, search: string) {
        if (!search) return false;
        const page = settings[index];

        if (page.name.toLowerCase().includes(search)) return false;

        for (const setting of Object.values(current[index])) {
            if (!filterSetting(search, setting)) return false;
        }

        return true;
    }

    function changeTab(index: number) {
        activeIndex = index;
        activePage = settings[activeIndex];
    }

    function apply() {
        current.forEach((page, index) => {
            for (const key in page) {
                settings[index].settings.value[key].value = page[key].value;
            }
            settings[index].settings.update(v => v);
        });

        pages.save();
        dispatch("save", {});
    }

    function save() {
        apply();
        close();
    }

    function close() {
        dispatch("close", {});
    }

    function refocusSearch() {
        searchElem.focus();
    }

    function changeTabById(page: string) {
        const target = settings.findIndex(p => p.id === page);
        if (target >= 0) {
            changeTab(target);
        }
    }

    onMount(() => {
        linkHandling.pageCallback = changeTabById;
        return () => linkHandling.pageCallback = undefined;
    });

</script>

<!----------------------------------------------------------------------------->

<div class="settings">
  <div class="sidebar" on:keydown={refocusSearch} tabindex="-1" role="textbox">
    <label class="input-field search">
      <i>🔍</i>
      <input bind:this={searchElem} type="search" bind:value={$search}>
    </label>

    {#each settings as page, index}
      <button class:filtered={filteredTabs[index]} on:click={() => changeTab(index)}
              class:active={activeIndex === index}>
        {page.name}
      </button>
    {/each}
  </div>
  <h2>{activePage.name}</h2>

  {#key activePage}
    <SettingsPage page={activePage} settings={Object.values(current[activeIndex])} />
  {/key}

  <div class="footer">
    <span class="version">{pages.footer ?? ""}</span>
    <button on:click={save}>Save</button>
    <button on:click={close}>Cancel</button>
    <button on:click={apply}>Apply</button>
  </div>
</div>

<!----------------------------------------------------------------------------->

<style>
    .settings {
        display: grid;
        grid-template:
                "sidebar header" max-content
                "sidebar page"   1fr
                "footer  footer" max-content /
                1fr 3fr;

        width: var(--window-width);

        border: calc((1 - var(--window-fullscreen)) * 2px) solid var(--gap-color-hard);
        background: var(--g100);

        height: 100%;
        box-sizing: border-box;
    }

    .sidebar {
        display: flex;
        flex-direction: column;
        border-right: 1px solid var(--gap-color);
        grid-area: sidebar;

        & .search {
            margin: 0.75rem 0.5rem;
        }
    }

    .sidebar > button {
        all: unset;
        height: 2em;
        padding-inline: 1em;
    }

    .sidebar > .active {
        background: #303030;
    }

    .sidebar > .filtered {
        opacity: 0.5;
    }

    .sidebar > button + button {
        /*border-top: 1px solid white;*/
    }

    h2 {
        margin: 0;
        padding: 0.5rem 1rem;
        grid-area: header;
        font-size: 1.5rem;
        font-weight: normal;
    }

    .footer {
        grid-area: footer;
        border-top: 1px solid var(--gap-color-hard);
        display: flex;
        align-items: center;

        & > .version {
            margin-right: auto;
            margin-left: 1ch;
            color: var(--t400);
        }
    }

    .footer > button {
        margin: 0.25em;
    }

</style>
